/**
 * Course card overview (program page) and course details + key learning areas (course detail page).
 * Key: programSlug/courseSlug matching backend slugs.
 */
export interface CourseCopy {
  cardOverview: string;
  details: string;
  keyLearningAreas: string[];
}

const COPY: Record<string, CourseCopy> = {
  'robotics/beginner-robotics': {
    cardOverview:
      'Learn the fundamentals of robotics by building and programming your first robots. Perfect for beginners who want to explore automation, electronics, and creative engineering.',
    details:
      'The Beginner Robotics program introduces students to the exciting world of robotics through hands-on learning. Over three weeks, learners explore how robots work, how sensors interact with environments, and how simple programs can control machines. Students will assemble basic robotic systems while learning core principles such as circuits, motors, sensors, and microcontrollers. By the end of the course, participants will have built and programmed simple robots capable of performing basic tasks.',
    keyLearningAreas: [
      'Introduction to robotics and automation',
      'Basic electronics and circuits',
      'Sensors and motors',
      'Robot assembly fundamentals',
      'Introductory programming for robotics',
      'Hands-on robot building',
    ],
  },
  'robotics/advanced-projects': {
    cardOverview:
      'Take your robotics skills further by designing and programming advanced robotic systems while working on real-world robotics projects.',
    details:
      'The Advanced Robotics program expands on the fundamentals and focuses on designing intelligent robotic systems. Students will work with more advanced sensors, controllers, and programming environments to build functional robotics applications. Participants will complete several guided projects including line-following robots, obstacle detection systems, and automation tasks.',
    keyLearningAreas: [
      'Advanced robot design',
      'Microcontroller programming',
      'Sensor integration',
      'Automation and robotics control',
      'Robotics troubleshooting',
      'Real-world robotics projects',
    ],
  },
  'robotics/master-projects': {
    cardOverview:
      'Build complete robotic systems and develop large-scale projects that demonstrate real-world robotics innovation.',
    details:
      'The Robotics Master Projects program is designed for learners ready to take robotics to a professional level. Students work on complex robotics systems involving AI integration, automation workflows, and multi-component robotics designs. This course is highly project-focused and encourages innovation, experimentation, and engineering problem-solving.',
    keyLearningAreas: [
      'Advanced robotics systems',
      'Robotics automation',
      'AI integration in robotics',
      'Mechanical design and prototyping',
      'Team-based robotics projects',
      'Final robotics showcase project',
    ],
  },
  'ai-tools-masterclass/foundations': {
    cardOverview:
      'Learn how modern AI tools are transforming industries and how to use them to boost productivity, creativity, and problem solving.',
    details:
      'The AI Tools Foundations program introduces participants to the rapidly growing world of artificial intelligence tools used in business, marketing, development, and content creation. Students will explore how AI works and how to apply it to automate tasks, generate ideas, analyze information, and improve efficiency.',
    keyLearningAreas: [
      'Introduction to artificial intelligence',
      'AI tools for productivity',
      'AI-powered content creation',
      'AI for research and data analysis',
      'Workflow automation with AI',
    ],
  },
  'ai-tools-masterclass/advanced-projects': {
    cardOverview:
      'Apply AI tools to real projects and build practical solutions across content, data, and automation.',
    details:
      'This program builds on AI foundations with project-based learning. Students work through real-world use cases, integrate AI into workflows, and develop portfolio-ready applications using modern AI platforms.',
    keyLearningAreas: [
      'Advanced AI tooling',
      'Project-based AI applications',
      'Integration and APIs',
      'Evaluation and iteration',
    ],
  },
  'ai-tools-masterclass/master-projects': {
    cardOverview:
      'Lead complex AI-driven projects and prepare for professional AI certifications and industry applications.',
    details:
      'The Master Projects track combines advanced AI concepts with certification preparation and industry use cases. Participants strengthen practical skills while building toward professional credentials and responsible AI use.',
    keyLearningAreas: [
      'AI concepts and frameworks',
      'Certification exam preparation',
      'AI tools practice labs',
      'Industry use cases',
      'AI ethics and responsible use',
    ],
  },
  'business-incubation-coaching/startup-basics-bootcamp': {
    cardOverview:
      'Turn your idea into a structured business plan and learn the essentials of launching a successful startup.',
    details:
      'This bootcamp helps aspiring entrepreneurs transform ideas into viable business concepts. Participants learn how to validate business ideas, develop business models, and create actionable launch strategies.',
    keyLearningAreas: [
      'Business idea validation',
      'Market research',
      'Business model development',
      'Branding and positioning',
      'Startup launch strategy',
    ],
  },
  'business-incubation-coaching/full-incubation': {
    cardOverview:
      'A comprehensive startup incubation program designed to help founders build, launch, and grow their businesses.',
    details:
      'The Full Incubation program provides hands-on mentorship, strategic support, and resources to help entrepreneurs build scalable startups. Participants work closely with mentors while developing business operations, marketing strategies, and growth plans.',
    keyLearningAreas: [
      'Product development',
      'Business strategy',
      'Customer acquisition',
      'Growth and scaling',
      'Startup mentorship',
    ],
  },
  'business-incubation-coaching/pitching-and-funding': {
    cardOverview:
      'Master the art of pitching your startup and learn how to secure funding from investors and partners.',
    details:
      'This course focuses on helping entrepreneurs present their ideas clearly and confidently to investors. Participants learn storytelling, financial projections, and investor communication strategies.',
    keyLearningAreas: [
      'Pitch deck creation',
      'Investor communication',
      'Financial projections',
      'Business storytelling',
      'Startup funding strategies',
    ],
  },
  'personal-mentorship-coaching/4-sessions': {
    cardOverview:
      'Personalized one-on-one mentorship sessions focused on your specific goals and challenges.',
    details:
      'These mentorship sessions provide direct access to experienced mentors who guide participants through career, business, or technical challenges. Each session focuses on strategy, growth, and actionable solutions tailored to the participant.',
    keyLearningAreas: ['Goal setting and strategy', 'Personalized action plans', 'Accountability and review', 'Expert guidance'],
  },
  'personal-mentorship-coaching/monthly-mentorship': {
    cardOverview:
      'Ongoing mentorship designed to support continuous growth, accountability, and professional development.',
    details:
      'Participants receive monthly guidance, strategic advice, and progress reviews to help them stay on track with their personal or professional goals.',
    keyLearningAreas: ['Ongoing strategy', 'Progress reviews', 'Accountability', 'Continuous development'],
  },
  'computer-packages/complete-package': {
    cardOverview:
      'Master essential office productivity tools used in businesses worldwide.',
    details:
      'This course teaches practical skills in document creation, spreadsheets, presentations, and digital workplace productivity.',
    keyLearningAreas: [
      'Word processing',
      'Spreadsheets and data management',
      'Presentation design',
      'Office productivity workflows',
    ],
  },
  'photography-videography-drone/photography-videography-fundamentals': {
    cardOverview:
      'Learn the fundamentals of visual storytelling, camera operation, lighting, and composition.',
    details:
      'Students learn how to capture professional-quality photos and videos using modern cameras and editing tools.',
    keyLearningAreas: ['Camera operation', 'Lighting and composition', 'Visual storytelling', 'Basic editing'],
  },
  'photography-videography-drone/advanced-photography-projects': {
    cardOverview:
      'Improve your photography skills while building a strong creative portfolio.',
    details:
      'Students work on advanced techniques such as lighting control, portrait photography, event coverage, and editing.',
    keyLearningAreas: ['Advanced lighting', 'Portrait and event photography', 'Portfolio development', 'Professional editing'],
  },
  'photography-videography-drone/advanced-videography-projects': {
    cardOverview:
      'Master cinematic video production from planning to final editing.',
    details:
      'This course covers filming techniques, storytelling, editing workflows, and professional video production.',
    keyLearningAreas: ['Filming techniques', 'Storytelling', 'Editing workflows', 'Professional production'],
  },
  'photography-videography-drone/drone-basics-safety': {
    cardOverview:
      'Learn how to safely operate drones for photography, videography, and inspections.',
    details:
      'Students learn drone flight principles, safety regulations, and aerial photography basics.',
    keyLearningAreas: ['Flight principles', 'Safety regulations', 'Aerial photography basics', 'Drone operation'],
  },
  'photography-videography-drone/drone-master-projects': {
    cardOverview:
      'Become an expert drone pilot while working on real aerial photography and video projects.',
    details:
      'Advanced flight training combined with real-world drone photography and mapping projects.',
    keyLearningAreas: ['Advanced flight', 'Aerial photography', 'Mapping projects', 'Professional applications'],
  },
  'music-classes/guitar': {
    cardOverview:
      'Learn guitar basics including chords, rhythm, and simple songs.',
    details:
      'Students practice playing techniques while building confidence performing music.',
    keyLearningAreas: ['Chords and rhythm', 'Simple songs', 'Playing technique', 'Performance confidence'],
  },
  'music-classes/keyboard': {
    cardOverview:
      'Learn to play keyboard while understanding musical notes, rhythm, and melodies.',
    details:
      'Students practice basic music theory and keyboard performance skills.',
    keyLearningAreas: ['Musical notes', 'Rhythm and melodies', 'Music theory', 'Keyboard performance'],
  },
  'music-classes/monthly-lessons': {
    cardOverview:
      'Ongoing music training for continuous improvement and performance practice.',
    details:
      'Monthly lessons support continuous growth and refinement of musical skills.',
    keyLearningAreas: ['Continuous practice', 'Skill refinement', 'Performance development'],
  },
  'technical-skills-package/cctv-installation-diagnostics': {
    cardOverview:
      'Learn how to install, configure, and troubleshoot CCTV security systems.',
    details:
      'Students gain practical experience installing surveillance systems used in homes and businesses.',
    keyLearningAreas: ['CCTV installation', 'Configuration', 'Troubleshooting', 'Surveillance systems'],
  },
  'technical-skills-package/repairs-maintenance': {
    cardOverview:
      'Develop practical skills for diagnosing and repairing computer hardware and printers.',
    details:
      'Hands-on training in computer and printer repair, diagnostics, and maintenance.',
    keyLearningAreas: ['Hardware diagnostics', 'Repair techniques', 'Printer maintenance', 'Computer repair'],
  },
  'technical-skills-package/phone-repairs': {
    cardOverview:
      'Learn smartphone repair techniques including screen replacement, diagnostics, and component repair.',
    details:
      'Practical training in phone disassembly, screen replacement, and component-level repair.',
    keyLearningAreas: ['Screen replacement', 'Diagnostics', 'Component repair', 'Smartphone hardware'],
  },
  'technical-skills-package/full-technical-package': {
    cardOverview:
      'A complete technical training program covering multiple repair and installation skills.',
    details:
      'Comprehensive training across CCTV, computers, printers, and phones for a full technical skill set.',
    keyLearningAreas: ['Multiple technical skills', 'Installation and repair', 'Integrated training'],
  },
  'coding-fundamentals/beginner-coding': {
    cardOverview:
      'Start your programming journey by learning coding logic and basic programming concepts.',
    details:
      'An introduction to programming logic, syntax, and problem-solving with code. Ideal for complete beginners.',
    keyLearningAreas: ['Coding logic', 'Basic syntax', 'Problem solving', 'First programs'],
  },
  'coding-fundamentals/intermediate-level': {
    cardOverview:
      'Build real applications while expanding your programming knowledge.',
    details:
      'Expand your skills by building real applications and learning intermediate concepts and patterns.',
    keyLearningAreas: ['Real applications', 'Intermediate concepts', 'Design patterns', 'Larger projects'],
  },
  'coding-fundamentals/advanced-projects': {
    cardOverview:
      'Develop full projects and strengthen your coding portfolio.',
    details:
      'Project-based learning to build portfolio-ready applications and advanced development skills.',
    keyLearningAreas: ['Full projects', 'Portfolio development', 'Advanced patterns', 'Deployment'],
  },
  'systems-web-development/web-development-bootcamp': {
    cardOverview:
      'Learn how to design and build professional websites from scratch.',
    details:
      'From design to deployment: build professional websites using modern tools and best practices.',
    keyLearningAreas: ['Web design', 'HTML/CSS/JS', 'Responsive design', 'Deployment'],
  },
  'systems-web-development/full-stack-track': {
    cardOverview:
      'Master both front-end and back-end web development technologies.',
    details:
      'Full-stack training covering front-end frameworks and back-end systems for complete web applications.',
    keyLearningAreas: ['Front-end frameworks', 'Back-end development', 'Databases', 'Full-stack applications'],
  },
  'cybersecurity-ethical-hacking/cybersecurity-basics': {
    cardOverview:
      'Learn how to protect systems and networks from cyber threats.',
    details:
      'Foundational skills in security concepts, threat awareness, and defensive practices.',
    keyLearningAreas: ['Security fundamentals', 'Threat awareness', 'Defensive practices', 'Network security'],
  },
  'cybersecurity-ethical-hacking/ethical-hacking-practical': {
    cardOverview:
      'Understand hacking techniques in order to identify and fix security vulnerabilities.',
    details:
      'Hands-on ethical hacking to find and remediate vulnerabilities in systems and networks.',
    keyLearningAreas: ['Vulnerability assessment', 'Ethical hacking techniques', 'Remediation', 'Practical labs'],
  },
  'cybersecurity-ethical-hacking/hacking-masterclass-hackathons': {
    cardOverview:
      'Advanced cybersecurity training with real hacking simulations and competitions.',
    details:
      'Master-level training with simulations and hackathons to test skills in realistic scenarios.',
    keyLearningAreas: ['Advanced techniques', 'Simulations', 'Hackathons', 'Real-world scenarios'],
  },
  'data-science-analytics/data-analysis-foundations': {
    cardOverview:
      'Learn how to analyze data and generate insights using modern data tools.',
    details:
      'Introduction to data analysis, visualization, and insight generation with industry-standard tools.',
    keyLearningAreas: ['Data analysis', 'Visualization', 'Insight generation', 'Data tools'],
  },
  'data-science-analytics/advanced-data-science': {
    cardOverview:
      'Master machine learning, predictive analytics, and advanced data science techniques.',
    details:
      'Advanced topics including machine learning, predictive models, and production data science.',
    keyLearningAreas: ['Machine learning', 'Predictive analytics', 'Advanced techniques', 'Production workflows'],
  },
  'networking-courses/networking-basics': {
    cardOverview:
      'Understand how computer networks operate and how to set up basic networking infrastructure.',
    details:
      'Students learn networking concepts including IP addressing, routers, switches, and network troubleshooting.',
    keyLearningAreas: ['Network concepts', 'IP addressing', 'Routers and switches', 'Troubleshooting'],
  },
  'networking-courses/certification-preparation': {
    cardOverview:
      'Prepare for professional networking and AI-related certifications while mastering practical skills.',
    details:
      'This program prepares learners for certification exams while strengthening practical knowledge of networking and related technologies used in modern industries.',
    keyLearningAreas: [
      'Certification exam preparation',
      'Practice labs',
      'Industry use cases',
      'Responsible use and best practices',
    ],
  },
};

function key(programSlug: string, courseSlug: string): string {
  return `${programSlug.toLowerCase()}/${courseSlug.toLowerCase()}`;
}

export function getCourseCopy(programSlug: string, courseSlug: string): CourseCopy | null {
  return COPY[key(programSlug, courseSlug)] ?? null;
}

export function getCardOverview(programSlug: string, courseSlug: string): string | null {
  const c = getCourseCopy(programSlug, courseSlug);
  return c?.cardOverview ?? null;
}

export function getCourseDetails(programSlug: string, courseSlug: string): { details: string; keyLearningAreas: string[] } | null {
  const c = getCourseCopy(programSlug, courseSlug);
  if (!c) return null;
  return { details: c.details, keyLearningAreas: c.keyLearningAreas };
}
